import {
  useCallback,
  useEffect,
  useRef,
  useState,
  Dispatch,
  MutableRefObject,
} from 'react';
import { captureMessage } from '@sentry/react';
import getUuidByString from 'uuid-by-string';
import {
  Card,
  ChargeVerifyBuyerDetails,
  StoreVerifyBuyerDetails,
  Payments,
  PaymentRequestOptions,
  ApplePay,
  CashAppPay,
  GooglePay,
  CashAppPayButtonOptions,
} from '@square/web-payments-sdk-types';

// TODO: Migrate utilities to TypeScript
import { localizedShopPath, getLocaleString } from 'utilities/locales';
import { PAY_WITH_CASH, isFeatureEnabled } from 'utilities/features';

import { useCheckoutFlow } from 'typescript/context/checkout_flow';
import { useLocaleInfo } from 'typescript/context/locale_info';
import { CountryCode } from 'typescript/constants/localization';
import { CardIntent } from 'typescript/constants/enums/card';
import { Env, getEnvironment } from 'typescript/utilities/environment';
import { TOrderSummary, OrderAddress } from 'typescript/types/api/solidshop';
import { hasVerifiableBillingAddress } from 'typescript/utilities/address_utils';

const paymentFormDivId = 'empty-div-for-payment-form';
const hiddenGooglePayTestDivId = 'hidden-div-for-gpay-test';
const cashAppPayDivId = 'pay-with-cash-app';

const createPaymentRequestPayload = (
  orderData: TOrderSummary,
  storeCode: CountryCode,
): PaymentRequestOptions => ({
  requestBillingContact: true,
  currencyCode: orderData.payment_due.currency,
  countryCode: storeCode,
  total: {
    label: 'SQUARE SHOP',
    amount: orderData.payment_due.amount,
    pending: false,
  },
  lineItems: [
    {
      label: 'Subtotal',
      amount: orderData.payment_due.amount,
      pending: false,
    },
  ],
});

// Drop null/undefined/empty values so that spreading an address over the contact
// details can never overwrite a populated contact field (email/phone/name) with an
// absent address field.
const omitEmptyValues = <T extends object>(obj?: T): T =>
  Object.fromEntries(
    Object.entries(obj ?? {}).filter(
      ([, value]) =>
        value !== undefined &&
        value !== null &&
        (typeof value !== 'string' || value.trim() !== ''),
    ),
  ) as T;

// Prefer the buyer's real billing address (card form, or wallet-provided and
// often postal-only); fall back to pickup/shipping only when it has no signal.
const createVerificationDetails = (
  orderData: TOrderSummary,
  cardIntent: CardIntent,
  billingAddress?: OrderAddress,
): ChargeVerifyBuyerDetails | StoreVerifyBuyerDetails => {
  const isBopis = orderData.bopis_fulfillment_selected;

  // The billing form does not collect country (it is scoped to the store's
  // market), so resolve it from the fulfillment address when the billing address
  // omits it.
  const fallbackCountryCode = isBopis
    ? orderData.pickup_location_info?.country_code
    : orderData.shipping_address?.country_code;

  const addressDetails = hasVerifiableBillingAddress(billingAddress)
    ? billingAddress
    : isBopis
      ? {
          ...orderData.pickup_location_info,
          locality: orderData.pickup_location_info?.city,
          administrative_district_level_1:
            orderData.pickup_location_info?.state,
        }
      : orderData.shipping_address;

  const contactDetails = isBopis
    ? orderData.pickup_person_info
    : orderData.contact_information;

  const combinedDetails = {
    ...contactDetails,
    ...omitEmptyValues(addressDetails),
  };

  const commonDetails = {
    squareProduct: 'MARKET',
    billingContact: {
      familyName: combinedDetails.last_name,
      givenName: combinedDetails.first_name,
      email: combinedDetails.email,
      countryCode: combinedDetails.country_code || fallbackCountryCode,
      city: combinedDetails.locality,
      // Wallet billing contacts are often postal-only; drop empty lines so the
      // SDK never receives [null, ''].
      addressLines: [
        combinedDetails.address_line_1,
        combinedDetails.address_line_2,
      ].filter(Boolean),
      postalCode: combinedDetails.postal_code,
      phone: combinedDetails.phone,
      state: combinedDetails.administrative_district_level_1,
    },
    ...([CardIntent.CHARGE, CardIntent.CHARGE_AND_STORE].includes(cardIntent)
      ? {
          amount: orderData.payment_due.amount,
          currencyCode: orderData.payment_due.currency,
        }
      : {}),
  };
  if (cardIntent === CardIntent.STORE) {
    return {
      ...commonDetails,
      intent: 'STORE',
    } as StoreVerifyBuyerDetails;
  } else {
    return {
      ...commonDetails,
      intent: cardIntent,
    } as ChargeVerifyBuyerDetails;
  }
};

const initGooglePay = async (
  payments: Payments,
  paymentRequest: PaymentRequestOptions,
): Promise<GooglePay> | null => {
  try {
    const googlePay = await payments.googlePay(
      payments.paymentRequest(paymentRequest),
    );
    await googlePay.attach(`#${hiddenGooglePayTestDivId}`);
    return googlePay;
  } catch {
    // if it fails it just means Google Pay is not supported
    return null;
  }
};

const initApplePay = async (
  payments: Payments,
  paymentRequest: PaymentRequestOptions,
): Promise<ApplePay> | null => {
  try {
    return await payments.applePay(payments.paymentRequest(paymentRequest));
  } catch {
    // if it fails it just means Apple Pay is not supported
    return null;
  }
};

interface CashAppPayParams {
  redirectURL: string;
  referenceId: string;
}

const cashAppPayParams = (
  redirectPath: string,
  referenceId: string,
): CashAppPayParams => {
  const redirectURL = `${window.location.origin}${redirectPath}`;

  return {
    redirectURL,
    referenceId,
  };
};

const initCashAppPay = async (
  payments: Payments,
  paymentRequest: PaymentRequestOptions,
  params: CashAppPayParams,
): Promise<CashAppPay> | null => {
  try {
    return await payments.cashAppPay(
      payments.paymentRequest(paymentRequest),
      params,
    );
  } catch {
    return null;
  }
};

const attachCashAppPay = (cashAppPay: CashAppPay): void => {
  const options: CashAppPayButtonOptions = {
    shape: 'semiround',
    width: 'full',
  };

  const cashAppDiv = document.getElementById(cashAppPayDivId);
  cashAppDiv.innerHTML = ''; // Clear existing content
  cashAppPay.attach(`#${cashAppPayDivId}`, options);
};

const paymentSdkScriptId = 'web-payments-sdk-script';

// The script can 200 with a non-JS body (in-app webviews behind proxies or
// content filters), in which case onload fires but the global never attaches —
// only this check proves the SDK is actually usable.
const paymentSdkAvailable = (): boolean =>
  typeof (window as any).Square?.payments === 'function';

// The script tag survives unmounts so the SDK survives route changes within
// checkout — which means a load can settle while no payment component is
// mounted. A completed tag never re-fires load/error, so the outcome must live
// at module level rather than in per-mount DOM listeners, or the next mount
// would wait forever on events that already fired.
let sdkLoadPromise: Promise<void> | null = null;
let sdkLoadAttempts = 0;

// Drops the memoized attempt and its tag so the next load starts fresh. Needed
// by retries, including after a "successful" load whose global has since gone
// missing (only a re-fetched script can re-attach it).
const resetPaymentSdkLoader = (): void => {
  sdkLoadPromise = null;
  document.getElementById(paymentSdkScriptId)?.remove();
};

const loadPaymentSdkScript = (): Promise<void> => {
  if (!sdkLoadPromise) {
    sdkLoadAttempts += 1;
    const attempt = sdkLoadAttempts;
    const script = document.createElement('script');
    const promise = new Promise<void>((resolve, reject) => {
      script.id = paymentSdkScriptId;
      script.src =
        getEnvironment() === Env.PRODUCTION
          ? 'https://web.squarecdn.com/v1/square.js'
          : 'https://staging.web.squarecdn.com/v1/square.js';
      script.async = true;
      script.addEventListener('load', () => {
        if (paymentSdkAvailable()) {
          resolve();
        } else {
          reject(new Error('script loaded without window.Square.payments'));
        }
      });
      script.addEventListener('error', () =>
        reject(new Error('script error event')),
      );
      document.head.appendChild(script);
    });
    promise.catch((error: Error) => {
      // Remove the dead tag and clear the memo so the next mount or retry
      // starts a fresh attempt — unless a retry already superseded this one.
      if (sdkLoadPromise === promise) {
        sdkLoadPromise = null;
      }
      script.remove();
      captureMessage('Square Web Payments SDK failed to load', {
        extra: { reason: error.message, attempt },
      });
    });
    sdkLoadPromise = promise;
  }
  // Re-check on every subscription: the memoized success may predate a global
  // that has since been clobbered.
  return sdkLoadPromise.then(() => {
    if (!paymentSdkAvailable()) {
      throw new Error('window.Square.payments went missing after load');
    }
  });
};

interface PaymentSdkStatus {
  scriptLoaded: boolean;
  loadFailed: boolean;
  retryLoad: () => void;
}

// TODO: Investigate if mounting payment form can be done in a more performant way.
const usePaymentSdk = (): PaymentSdkStatus => {
  const [scriptLoaded, setScriptLoaded] = useState(paymentSdkAvailable);
  const [loadFailed, setLoadFailed] = useState(false);
  const [loadAttempt, setLoadAttempt] = useState(0);

  useEffect(() => {
    if (paymentSdkAvailable()) {
      setScriptLoaded(true);
      return undefined;
    }

    // Drop a stale true (retry after the global went missing) back to loading
    // so consumers re-run their init effects when the fresh load lands.
    setScriptLoaded(false);

    let disposed = false;
    loadPaymentSdkScript()
      .then(() => {
        if (!disposed) setScriptLoaded(true);
      })
      .catch(() => {
        if (!disposed) setLoadFailed(true);
      });

    return () => {
      disposed = true;
    };
  }, [loadAttempt]);

  const retryLoad = useCallback(() => {
    // Checkout routes mount only one SDK consumer at a time. Retrying resets
    // the shared loader, then re-subscribes this mounted consumer via state.
    resetPaymentSdkLoader();
    setLoadFailed(false);
    setLoadAttempt((attempt) => attempt + 1);
  }, []);

  return { scriptLoaded, loadFailed, retryLoad };
};

interface PaymentForm {
  sqPayments: MutableRefObject<Payments>;
  sqCard: Card;
  googlePay: GooglePay;
  applePay: ApplePay;
  cashAppPay: CashAppPay;
  initFailed: boolean;
}

const usePaymentForm = (
  scriptLoaded: boolean,
  orderData: TOrderSummary,
): PaymentForm => {
  const { orderNumber, applicationId, locationId } = useCheckoutFlow();
  const { countryCode } = useLocaleInfo();

  const sqPayments = useRef<Payments | null>(null);

  // Payment Methods
  const [sqCard, setSqCard] = useState<any>(null);
  const [googlePay, setGooglePay] = useState<any>(null);
  const [applePay, setApplePay] = useState<any>(null);
  const [cashAppPay, setCashAppPay] = useState<any>(null);
  const [initFailed, setInitFailed] = useState(false);

  useEffect(() => {
    if (!scriptLoaded || !orderData || Object.entries(orderData).length === 0) {
      return;
    }

    // scriptLoaded implies the global existed moments ago, but if it has gone
    // missing anyway, surface the recoverable failure (banner + retry) instead
    // of tripping the error boundary or silently dead-ending with no form.
    if (!paymentSdkAvailable()) {
      setInitFailed(true);
      captureMessage(
        'window.Square.payments unavailable although the SDK script loaded',
      );
      return;
    }
    setInitFailed(false);

    const payments = (window as any).Square.payments(
      applicationId,
      locationId,
    ) as Payments;

    sqPayments.current = payments;
    payments.setLocale(getLocaleString());

    const cardPromise = payments.card();
    let applePayPromise, cashAppPayPromise, googlePayPromise;

    if (
      !orderData.upfront_card_payment_only &&
      !orderData.first_party_payment_only &&
      !orderData.has_subscription &&
      orderData.payment_due.payment_schedule !== 'installment'
    ) {
      const paymentRequest = createPaymentRequestPayload(
        orderData,
        countryCode,
      );
      googlePayPromise = initGooglePay(payments, paymentRequest);
      applePayPromise = initApplePay(payments, paymentRequest);

      if (isFeatureEnabled(PAY_WITH_CASH)) {
        const cashAppPaymentRequest = createPaymentRequestPayload(
          orderData,
          countryCode,
        );

        const params = cashAppPayParams(
          `${localizedShopPath()}/checkout/v3/cash_app_pay`,
          getUuidByString(orderNumber + orderData.total.amount),
        );

        cashAppPayPromise = initCashAppPay(
          payments,
          cashAppPaymentRequest,
          params,
        );
      }
    }

    cardPromise.then((result: any) => {
      setSqCard(result);
      const paymentFormDiv = document.getElementById(paymentFormDivId);
      if (paymentFormDiv) {
        paymentFormDiv.innerHTML = ''; // Clear existing content
        result.attach(`#${paymentFormDivId}`);
      }
    });

    const addDigitalWallet = (setter: Dispatch<any>) => (wallet: any) => {
      if (wallet) setter(wallet);
    };
    googlePayPromise?.then(addDigitalWallet(setGooglePay));
    applePayPromise?.then(addDigitalWallet(setApplePay));
    cashAppPayPromise?.then(addDigitalWallet(setCashAppPay));
  }, [scriptLoaded, orderData]);

  return {
    sqPayments,
    sqCard,
    googlePay,
    applePay,
    cashAppPay,
    initFailed,
  };
};

export {
  createPaymentRequestPayload,
  createVerificationDetails,
  initGooglePay,
  initApplePay,
  initCashAppPay,
  cashAppPayParams,
  attachCashAppPay,
  paymentSdkAvailable,
  resetPaymentSdkLoader,
  usePaymentSdk,
  usePaymentForm,
  paymentFormDivId,
  hiddenGooglePayTestDivId,
  cashAppPayDivId,
};
